import { Button, Dialog, MenuItem, MenuTrigger, Popover, Progress } from '@eleveo/components-react';
import { HomeIcon, ThreeDotsIcon } from '@eleveo/icons-react';
import classnames from 'classnames';
import { addDays, format, isBefore, isSameDay, startOfToday } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { pathOr } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { UncontrolledTooltip } from 'reactstrap';

import { Permissions, useAuth } from '../../../app/AuthorizationProvider';
import { PTO_REQUEST_OPTIONS, REQUESTER_SHIFT_SWAP_REQUEST_PROPS } from '../../../config/agentsRequests';
import {
  GetAgentProductionScheduleComponent,
  RequestSubtype,
  RequestType,
  ScheduleDailyOverview,
  ScheduleElement,
} from '../../../graphql/components';
import { PATH_MY_SCHEDULE } from '../../../router/routes';
import { formatScheduleElementRGBAColor } from '../../../utils/chart';
import { BriefScheduleOverview } from '../BriefScheduleOverviewNext';
import { transformScheduleElementsOnly, translateScheduleElementName } from '../transformData';
import { formatShiftDuration, getScheduleElement } from './utils';

import type { PopoverProps } from '@eleveo/components-react';

interface DayTileProps {
  activeDate: Date | null;
  date: Date;
  initiateCreationOfNewRequest: (type: RequestType, subtype: RequestSubtype, date: Date) => void;
  scheduleItem?: ScheduleDailyOverview;
  scheduleElements: ScheduleElement[];
  timezone: string;
  onClose: () => void;
}

export const ShiftPart: React.FC<{ scheduleElement: ScheduleElement; small?: boolean }> = ({
  scheduleElement,
  small,
}) => {
  const { t } = useTranslation();
  const elementName = translateScheduleElementName(t, scheduleElement);

  return (
    <div
      className={classnames('monthly-calendar__content-part', { 'monthly-calendar__content-part--small': small })}
      style={{
        backgroundColor: formatScheduleElementRGBAColor(scheduleElement.color, 0.25),
      }}
    >
      <strong>{small ? elementName.slice(0, 2) : elementName}</strong>
    </div>
  );
};

export const DayTile: React.FC<DayTileProps> = ({
  activeDate,
  date,
  initiateCreationOfNewRequest,
  scheduleItem,
  scheduleElements,
  timezone,
  onClose,
}) => {
  const { t } = useTranslation();
  const { userId: agentId, localeObj, userHasPermissions } = useAuth();
  const targetRef = React.useRef(null);

  const isOpen = activeDate && isSameDay(date, activeDate);
  const tileId = scheduleItem ? new Date(scheduleItem.date).getTime() : null;

  const shiftDuration =
    scheduleItem && scheduleItem.shift ? formatShiftDuration(scheduleItem.shift.durationShiftHours, t) : null;

  const zonedStartDate = zonedTimeToUtc(date, timezone);

  const endDate = addDays(date, 1);
  const zonedEndDate = zonedTimeToUtc(endDate, timezone);

  // Design supports only one element before main shift
  const beforeScheduleElement =
    scheduleItem && getScheduleElement(scheduleItem.beforeShiftScheduleElementIds[0], scheduleElements);
  // Design supports only one element after main shift
  const afterScheduleElement =
    scheduleItem && getScheduleElement(scheduleItem.afterShiftScheduleElementIds[0], scheduleElements);

  const hasPermissionToSubmitPTORequest = userHasPermissions([Permissions.PtoRequestsAccess]);
  const hasPermissionToSubmitShiftSwapRequest = userHasPermissions([Permissions.ShiftSwapRequestsAccess]);

  return (
    <div className="monthly-calendar__content-wrapper" data-test-id={format(date, 'yyyy-MM-dd')} ref={targetRef}>
      <MenuTrigger
        items={
          [
            ...PTO_REQUEST_OPTIONS.map(
              (requestOption) =>
                hasPermissionToSubmitPTORequest && {
                  id: requestOption.subtype,
                  icon: requestOption.icon,
                  onClick: () => initiateCreationOfNewRequest(requestOption.type, requestOption.subtype, date),
                  testId: `agent-portal:monthly-view:create-new-${requestOption.subtype.toLowerCase()}-request`,
                  text: t(requestOption.nameKey),
                }
            ),
            hasPermissionToSubmitShiftSwapRequest && {
              id: RequestType.ShiftSwap,
              icon: REQUESTER_SHIFT_SWAP_REQUEST_PROPS.icon,
              onClick: () =>
                initiateCreationOfNewRequest(
                  REQUESTER_SHIFT_SWAP_REQUEST_PROPS.type,
                  REQUESTER_SHIFT_SWAP_REQUEST_PROPS.subtype,
                  date
                ),
              testId: `agent-portal:monthly-view:create-new-${REQUESTER_SHIFT_SWAP_REQUEST_PROPS.type.toLowerCase()}-request`,
              text: t(REQUESTER_SHIFT_SWAP_REQUEST_PROPS.nameKey),
              isDisabled: isBefore(date, startOfToday()) || !scheduleItem?.shift?.swappable,
            },
          ].filter(Boolean) as MenuItem[]
        }
      >
        <Button
          aria-label={t('layout.action.actions')}
          className="monthly-calendar__day__actions-menu"
          isDisabled={!hasPermissionToSubmitPTORequest && !hasPermissionToSubmitShiftSwapRequest}
          isSmall
          testId="calendar-day-tile:actions"
          variant="quiet"
        >
          <ThreeDotsIcon />
        </Button>
      </MenuTrigger>

      {scheduleItem?.settings?.remote && (
        <div className="monthly-calendar__workplace" title={t('schedule.workingRemotely')}>
          <HomeIcon className="text-green-700" />
        </div>
      )}

      {scheduleItem && (
        <div className="monthly-calendar__content">
          {beforeScheduleElement && <ShiftPart scheduleElement={beforeScheduleElement} small={!!scheduleItem.shift} />}
          {scheduleItem.shift && (
            <div className="monthly-calendar__content-part monthly-calendar__content-part--shift">
              <dl className="shift">
                <div className="shift__row">
                  <dt className="shift__title">{t('time.start')}</dt>
                  <dd className="shift__time">
                    {format(
                      utcToZonedTime(scheduleItem.shift.dateTimeRange.startDateTime, timezone),
                      t('date.formats.timeShort'),
                      {
                        locale: localeObj,
                      }
                    )}
                  </dd>
                </div>
                <div className="shift__row">
                  <dt className="shift__title">{t('time.end')}</dt>
                  <dd className="shift__time">
                    {format(
                      utcToZonedTime(scheduleItem.shift.dateTimeRange.endDateTime, timezone),
                      t('date.formats.timeShort'),
                      {
                        locale: localeObj,
                      }
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          )}
          {afterScheduleElement && <ShiftPart scheduleElement={afterScheduleElement} small />}
        </div>
      )}

      <div className="monthly-calendar__summary">
        {scheduleItem && scheduleItem.overviewScheduleElementIds.length > 0 && (
          <ul className="monthly-calendar__schedule-elements">
            {scheduleItem.overviewScheduleElementIds.map((scheduleElementId) => {
              const scheduleElement = getScheduleElement(scheduleElementId, scheduleElements);
              const scheduleElementTileId = `id${tileId}${scheduleElementId}`;

              if (!scheduleElement) {
                return null;
              }

              const elementName = translateScheduleElementName(t, scheduleElement);

              return (
                scheduleElement && (
                  <li
                    className="monthly-calendar__schedule-element"
                    style={{
                      backgroundColor: formatScheduleElementRGBAColor(scheduleElement.color),
                    }}
                    key={scheduleElementTileId}
                    id={scheduleElementTileId}
                  >
                    <span className="sr-only">{elementName}</span>
                    <UncontrolledTooltip target={scheduleElementTileId} delay={0}>
                      {elementName}
                    </UncontrolledTooltip>
                  </li>
                )
              );
            })}
          </ul>
        )}
        <dl className="shift shift--right">
          <div className="shift__row">
            <dt className="shift__title">{t('shiftTemplate.shift', { count: 1 })}</dt>
            <dd className="shift__time">{shiftDuration || t('layout.none')}</dd>
          </div>
        </dl>
      </div>

      {scheduleItem && isOpen && (
        <GetAgentProductionScheduleComponent
          variables={{
            agentId,
            timezone,
            startDateTime: zonedStartDate.toISOString(),
            endDateTime: zonedEndDate.toISOString(),
          }}
          fetchPolicy="network-only"
        >
          {(agentScheduleResult) => {
            const isLoading = agentScheduleResult.loading;
            const scheduleItems = pathOr([], ['data', 'agentProductionSchedule', 'scheduleItems'], agentScheduleResult);
            const elements = transformScheduleElementsOnly(scheduleItems, scheduleElements, t);
            return (
              <>
                {isLoading ? (
                  <Progress withOverlay />
                ) : (
                  <Popover
                    triggerRef={targetRef}
                    placement="bottom"
                    state={{ isOpen, close: onClose } as PopoverProps['state']}
                  >
                    <Dialog>
                      <BriefScheduleOverview
                        convertToZonedDate={false}
                        date={date.getTime()}
                        elements={elements}
                        remote={scheduleItem.settings?.remote}
                        t={t}
                        timezone={timezone}
                      />

                      <Button
                        as={Link}
                        className="w-full mt-15"
                        testId="schedule-type-switcher-button"
                        to={{ pathname: `${PATH_MY_SCHEDULE}/weekly`, state: { date: zonedStartDate } }}
                      >
                        {t('layout.detailView')}
                      </Button>
                    </Dialog>
                  </Popover>
                )}
              </>
            );
          }}
        </GetAgentProductionScheduleComponent>
      )}
    </div>
  );
};
